import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useEffect, useRef, useState, useContext } from "react";
import { ethers } from "ethers";
import { AppContext } from "../AppContextProvider";
import { collectLog, createEmptyLogsAndVars, dwReportError, recordVariable, } from "../utils/diagnostics/clientErrors";
import { deleteMpcData } from "../processes/onboarding/onboarding";
import { isGuestRoute } from "../utils/guestRoute";
import { setAvalancheWriteAuthSigner } from "../avalanche/avalancheServerSigner";
import { checkWalletWhitelist, hasProtectedWalletAccess, useBackendAuthStatus, } from "./walletWhitelist";
import { clearDesktopAuthWallet, loadDesktopAuthWallet, storeDesktopAuthWallet, } from "./desktopAuthBinding";
export const WalletProviderContext = createContext({
    wallets: {},
    selectedWallet: null,
    selectedAccount: null,
    errorMessage: null,
    connectWallet: async (providerWithInfo, _callback) => {
        _callback();
        Promise.resolve();
    },
    disconnectWallet: () => { },
    clearError: () => { },
    isLoggedIn: () => false,
    isConnected: () => false,
    isWalletChanged: () => false,
    walletSessionVersion: 0,
    backendAuthStatus: "idle",
    isRestoringSession: false,
    allowInteractiveAuth: false,
    authoritativeBackendDenial: false,
    whitelistStatus: "idle",
    whitelistUnavailableReason: null,
    retryWhitelistCheck: () => { },
    setBackendAuthStatus: () => { },
});
export const WalletProvider = ({ children }) => {
    const [wallets, setWallets] = useState({});
    const [prevWalletRdns, setPrevWalletRdns] = useState(null);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [selectedWallet, setSelectedWallet] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [walletSessionVersion, setWalletSessionVersion] = useState(0);
    const [backendAuthStatus, setBackendAuthStatusState] = useState("idle");
    const [whitelistStatus, setWhitelistStatus] = useState("idle");
    const [whitelistUnavailableReason, setWhitelistUnavailableReason] = useState(null);
    const [whitelistRetry, setWhitelistRetry] = useState(0);
    const [isRestoringSession, setIsRestoringSession] = useState(() => typeof localStorage !== "undefined" && !!localStorage.getItem("selectedWalletRdns"));
    const [allowInteractiveAuth, setAllowInteractiveAuth] = useState(false);
    const connectAttemptProviderRef = useRef(null);
    const [authoritativeDenial, setAuthoritativeDenial] = useState(null);
    const backendAuthStatusForWallet = useBackendAuthStatus(selectedAccount, selectedWallet !== null, walletSessionVersion, backendAuthStatus);
    const context = useContext(AppContext);
    const whitelistSessionRef = useRef({
        account: selectedAccount?.toLowerCase() ?? null,
        version: walletSessionVersion,
    });
    whitelistSessionRef.current = {
        account: selectedAccount?.toLowerCase() ?? null,
        version: walletSessionVersion,
    };
    const clearError = () => setErrorMessage("");
    const setError = (error) => setErrorMessage(error);
    const invalidateBackendAuth = useCallback(() => {
        // Clear the ref-equivalent evidence before publishing any replacement
        // account/session state, so a denial can never flash for the next wallet.
        setAuthoritativeDenial(null);
        setBackendAuthStatusState("idle");
    }, []);
    const setBackendAuthStatus = useCallback((status, walletAddress, isAuthoritativeDenial = false) => {
        if (selectedWallet !== null &&
            selectedAccount !== null &&
            ethers.utils.isAddress(walletAddress) &&
            selectedAccount.toLowerCase() === walletAddress.toLowerCase()) {
            if (status === "denied" && isAuthoritativeDenial) {
                setAuthoritativeDenial({
                    walletAddress: walletAddress.toLowerCase(),
                    sessionVersion: walletSessionVersion,
                });
                setBackendAuthStatusState("denied");
            }
            else {
                setAuthoritativeDenial(null);
                setBackendAuthStatusState(status === "denied" ? "checking" : status);
            }
        }
    }, [selectedAccount, selectedWallet, walletSessionVersion]);
    const retryWhitelistCheck = useCallback(() => {
        setWhitelistRetry((attempt) => attempt + 1);
    }, []);
    useEffect(() => {
        const account = selectedAccount;
        const sessionVersion = walletSessionVersion;
        if (!selectedWallet || !account || !ethers.utils.isAddress(account)) {
            setWhitelistStatus("idle");
            setWhitelistUnavailableReason(null);
            return;
        }
        let active = true;
        setWhitelistStatus("checking");
        setWhitelistUnavailableReason(null);
        void checkWalletWhitelist(account).then((result) => {
            if (!active
                || whitelistSessionRef.current.account !== account.toLowerCase()
                || whitelistSessionRef.current.version !== sessionVersion)
                return;
            setWhitelistStatus(result.status);
            setWhitelistUnavailableReason(result.status === "unavailable" ? result.reason : null);
        });
        return () => { active = false; };
    }, [selectedAccount, selectedWallet, walletSessionVersion, whitelistRetry]);
    // Register the connected wallet as the auth signer for server-side
    // Avalanche writes: the orchestrator only signs transactions for requests
    // proven with a fresh personal_sign signature from a whitelisted wallet.
    useEffect(() => {
        if (selectedWallet && selectedAccount) {
            setAvalancheWriteAuthSigner({
                getAddress: () => selectedAccount,
                signMessage: async (message, address) => {
                    const hexMessage = "0x" +
                        Array.from(new TextEncoder().encode(message))
                            .map((b) => b.toString(16).padStart(2, "0"))
                            .join("");
                    return (await selectedWallet.provider.request({
                        method: "personal_sign",
                        params: [hexMessage, address],
                    }));
                },
            });
        }
        else {
            setAvalancheWriteAuthSigner(null);
        }
    }, [selectedWallet, selectedAccount]);
    useEffect(() => {
        if (selectedAccount
            && selectedWallet?.provider === connectAttemptProviderRef.current) {
            connectAttemptProviderRef.current = null;
        }
    }, [selectedAccount, selectedWallet]);
    useEffect(() => {
        if (!selectedWallet)
            return;
        const provider = selectedWallet.provider;
        const invalidate = () => setWalletSessionVersion((version) => version + 1);
        const handleChainChanged = () => {
            invalidateBackendAuth();
            setWhitelistStatus("checking");
            setWhitelistUnavailableReason(null);
            invalidate();
        };
        const handleAccountsChanged = (accounts) => {
            // MetaMask may emit accountsChanged before our own eth_requestAccounts
            // promise resolves. The promise result is authoritative for that connect
            // attempt; treating the event as an external switch disables interactive
            // auth before the signing flow can begin.
            if (connectAttemptProviderRef.current === provider)
                return;
            const nextAccount = Array.isArray(accounts) && typeof accounts[0] === "string" ? accounts[0] : null;
            const sameAccount = selectedAccount === null
                ? nextAccount === null
                : nextAccount?.toLowerCase() === selectedAccount.toLowerCase();
            if (sameAccount)
                return;
            const accountChanged = !!selectedAccount && !!nextAccount
                && selectedAccount.toLowerCase() !== nextAccount.toLowerCase();
            const accountAccessRemoved = !!selectedAccount && !nextAccount;
            const authWalletChanged = !!nextAccount
                && !!loadDesktopAuthWallet()
                && loadDesktopAuthWallet() !== nextAccount.toLowerCase();
            invalidateBackendAuth();
            setAllowInteractiveAuth(false);
            if (accountChanged || accountAccessRemoved || authWalletChanged) {
                sessionStorage.clear();
                deleteMpcData();
                clearDesktopAuthWallet();
                if (selectedWallet && nextAccount) {
                    localStorage.setItem("selectedWalletRdns", selectedWallet.info.rdns);
                    localStorage.setItem("selectedAccountByWalletRdns", JSON.stringify({ [selectedWallet.info.rdns]: nextAccount }));
                }
            }
            setWhitelistStatus(nextAccount ? "checking" : "idle");
            setWhitelistUnavailableReason(null);
            if (nextAccount)
                setBackendAuthStatusState("checking");
            setSelectedAccount(nextAccount);
            invalidate();
        };
        const handleDisconnect = () => {
            invalidateBackendAuth();
            setAllowInteractiveAuth(false);
            sessionStorage.clear();
            deleteMpcData();
            clearDesktopAuthWallet();
            setWhitelistStatus("idle");
            setWhitelistUnavailableReason(null);
            setSelectedAccount(null);
            setSelectedWallet(null);
            localStorage.removeItem("selectedWalletRdns");
            invalidate();
        };
        provider.on?.("accountsChanged", handleAccountsChanged);
        provider.on?.("chainChanged", handleChainChanged);
        provider.on?.("disconnect", handleDisconnect);
        return () => {
            provider.removeListener?.("accountsChanged", handleAccountsChanged);
            provider.removeListener?.("chainChanged", handleChainChanged);
            provider.removeListener?.("disconnect", handleDisconnect);
        };
    }, [selectedWallet, selectedAccount, invalidateBackendAuth]);
    const BEFORE_UNLOAD_TIME_KEY = "beforeUnloadTime";
    const SESSION_BACKGROUND_TIMEOUT_MS = 1200000;
    useEffect(() => {
        if (isGuestRoute()) {
            setIsRestoringSession(false);
            // Guest (magic-link) screens have no wallet: skip provider discovery
            // and auto-connect entirely so MetaMask never pops up.
            //
            // Safety net: if the app somehow SPA-navigates from a guest route into
            // the main application (history back/forward, or a future in-app link
            // that forgot to use exitGuestMode), the wallet layer was never
            // initialized. Detect leaving the guest routes and force a full reload
            // so initialization runs from scratch.
            const reloadIfLeftGuestRoute = () => {
                if (!isGuestRoute()) {
                    console.log("[WalletProvider] Left guest route via SPA navigation — reloading to initialize wallet layer");
                    window.location.reload();
                }
            };
            window.addEventListener("popstate", reloadIfLeftGuestRoute);
            const originalPushState = window.history.pushState.bind(window.history);
            const originalReplaceState = window.history.replaceState.bind(window.history);
            window.history.pushState = function (...args) {
                originalPushState(...args);
                reloadIfLeftGuestRoute();
            };
            window.history.replaceState = function (...args) {
                originalReplaceState(...args);
                reloadIfLeftGuestRoute();
            };
            return () => {
                window.removeEventListener("popstate", reloadIfLeftGuestRoute);
                window.history.pushState = originalPushState;
                window.history.replaceState = originalReplaceState;
            };
        }
        const now = Date.now();
        const beforeUnloadTime = localStorage.getItem(BEFORE_UNLOAD_TIME_KEY);
        if (beforeUnloadTime && now - parseInt(beforeUnloadTime, 10) > SESSION_BACKGROUND_TIMEOUT_MS) {
            console.log('[DesktopAuth] Session background timeout exceeded (' + SESSION_BACKGROUND_TIMEOUT_MS / 1000 + 's) — clearing wallet connection only');
            localStorage.removeItem("selectedWalletRdns");
            localStorage.removeItem("prevWalletRdns");
        }
        const updateLastActivity = () => {
            localStorage.setItem(BEFORE_UNLOAD_TIME_KEY, Date.now().toString());
        };
        // Keep the activity timestamp fresh while the tab is actually open.
        // Relying on `beforeunload` alone is not enough: a browser crash, OOM
        // tab-kill, or tab discard (frequent during memory-heavy calls) never
        // fires it, so the next startup would compare against a timestamp from
        // a much earlier unload and falsely clear the wallet connection even
        // though the user was active seconds earlier.
        updateLastActivity();
        const heartbeat = window.setInterval(() => {
            if (document.visibilityState === "visible") {
                updateLastActivity();
            }
        }, 60000);
        const onVisibilityChange = () => {
            // Record the moment the tab goes to the background so the timeout
            // measures real away-time.
            updateLastActivity();
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        window.addEventListener("beforeunload", updateLastActivity);
        const savedPrevWalletRdns = loadPrevWalletRdns();
        const savedSelectedWalletRdns = loadSelectedWalletRdns();
        setWallets({});
        let restoreSettled = !savedSelectedWalletRdns;
        let restoreAttemptStarted = false;
        const finishRestore = () => {
            if (restoreSettled)
                return;
            restoreSettled = true;
            setIsRestoringSession(false);
        };
        if (!savedSelectedWalletRdns) {
            setIsRestoringSession(false);
        }
        const restoreTimeout = window.setTimeout(finishRestore, 8000);
        function onAnnouncement(event) {
            //console.log("onAnnouncement started", event);
            setWallets((currentWallets) => ({
                ...currentWallets,
                [event.detail.info.rdns]: event.detail,
            }));
            if (savedSelectedWalletRdns
                && event.detail.info.rdns === savedSelectedWalletRdns
                && !restoreAttemptStarted) {
                restoreAttemptStarted = true;
                void connectWallet(event.detail, () => { }, true).finally(finishRestore);
            }
        }
        window.addEventListener("eip6963:announceProvider", onAnnouncement);
        //console.log("requestProvider");
        window.dispatchEvent(new Event("eip6963:requestProvider"));
        return () => {
            window.removeEventListener("eip6963:announceProvider", onAnnouncement);
            window.removeEventListener("beforeunload", updateLastActivity);
            window.clearInterval(heartbeat);
            window.clearTimeout(restoreTimeout);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, []);
    const connectWallet = useCallback(async (providerWithInfo, _callback, silentRestore = false) => {
        //console.log("meghívódik a connectWallet", providerWithInfo);
        if (!silentRestore)
            context.isLogin(true);
        const logsAndVars = createEmptyLogsAndVars("WalletProvider:connectWallet");
        try {
            const wallet = providerWithInfo;
            connectAttemptProviderRef.current = wallet.provider;
            setAllowInteractiveAuth(!silentRestore);
            updatePrevWalletRdns(loadSelectedWalletRdns());
            invalidateBackendAuth();
            setWhitelistStatus("idle");
            setWhitelistUnavailableReason(null);
            setSelectedAccount(null);
            setWalletSessionVersion((version) => version + 1);
            updateSelectedWallet(wallet);
            //console.log("prevWalletRdns", localStorage.getItem("prevWalletRdns"));
            //console.log("selectedWalletRdns", localStorage.getItem("selectedWalletRdns"));
            //console.log("setSelectedWallet run", wallet);
            const accounts = (await wallet.provider.request({
                method: silentRestore ? "eth_accounts" : "eth_requestAccounts",
            }));
            if (!accounts?.[0]) {
                connectAttemptProviderRef.current = null;
            }
            recordVariable(logsAndVars, "accounts", accounts);
            //const balance = await wallet.provider.request({method:'eth_getBalance', params: [accounts?.[0], "latest"]}) as string[]
            //console.log("accounts", accounts);
            if (accounts?.[0]) {
                const rawAccountMap = localStorage.getItem("selectedAccountByWalletRdns");
                let accountMap = {};
                try {
                    accountMap = rawAccountMap ? JSON.parse(rawAccountMap) : {};
                }
                catch {
                    accountMap = {};
                }
                const savedAccount = accountMap[wallet.info.rdns];
                if (silentRestore
                    && !loadDesktopAuthWallet()
                    && savedAccount?.toLowerCase() === accounts[0].toLowerCase()
                    && !!localStorage.getItem("refreshToken")) {
                    // One-time migration for sessions created before the explicit
                    // desktop token-to-wallet binding was introduced.
                    storeDesktopAuthWallet(accounts[0]);
                }
                const authWallet = loadDesktopAuthWallet();
                if ((savedAccount && savedAccount.toLowerCase() !== accounts[0].toLowerCase())
                    || (authWallet && authWallet !== accounts[0].toLowerCase())) {
                    sessionStorage.clear();
                    deleteMpcData();
                    clearDesktopAuthWallet();
                    localStorage.setItem("selectedWalletRdns", wallet.info.rdns);
                }
                accountMap[wallet.info.rdns] = accounts[0];
                localStorage.setItem("selectedAccountByWalletRdns", JSON.stringify(accountMap));
                setSelectedAccount(accounts[0]);
                setBackendAuthStatusState("checking");
                setWalletSessionVersion((version) => version + 1);
                recordVariable(logsAndVars, "selectedWalletRdns (saved to localstorage)", getSelectedWalletRdns());
                //localStorage.setItem("isLottieLookingAtIt", "false");
                recordVariable(logsAndVars, "isLottieLookingAtIt (saved to localstorage)", "false");
            }
            //localStorage.setItem("isConnected", "true");
            if (accounts?.[0])
                context.onLogIn();
            if (_callback !== undefined) {
                _callback();
            }
        }
        catch (error) {
            connectAttemptProviderRef.current = null;
            dwReportError(null, undefined, error, { logsAndVars });
            setSelectedWallet(null);
            setSelectedAccount(null);
            setAllowInteractiveAuth(false);
            setWhitelistStatus("idle");
            setWhitelistUnavailableReason(null);
            setWalletSessionVersion((version) => version + 1);
            console.error("Failed to connect to provider:", error);
            const walletError = error;
            setError(`Code: ${walletError.code} \nError Message: ${walletError.message}`);
        }
    }, [wallets, context, invalidateBackendAuth]);
    const disconnectWallet = useCallback(async () => {
        // console.log(selectedWalletRdns);
        //sessionStorage.removeItem("bubleFilter");
        //sessionStorage.removeItem("childrenClose");
        //sessionStorage.removeItem("bubleHidden");
        //sessionStorage.removeItem("actuelleCid");
        //sessionStorage.removeItem("notDisplayedBuble");
        //console.log("disconnectWallet");
        const logsAndVars = createEmptyLogsAndVars("WalletProvider:disconnectWallet");
        recordVariable(logsAndVars, "selectedWalletRdns", getSelectedWalletRdns());
        invalidateBackendAuth();
        setAllowInteractiveAuth(false);
        setWhitelistStatus("idle");
        setWhitelistUnavailableReason(null);
        setSelectedAccount(null);
        setWalletSessionVersion((version) => version + 1);
        if (selectedWallet) {
            collectLog(logsAndVars, "Removing localStorage values");
            context.onLogout();
            context.isLogin(false);
            setSelectedWallet(null);
            //updatePrevWalletRdns(null);
            sessionStorage.clear();
            deleteMpcData();
            clearDesktopAuthWallet();
            /*window.ethereum.on("disconnect", () => {
              console.log("lost connection to the rpc");
            });*/
            /*
           if (wallet) {
              collectLog(logsAndVars, "We had a wallet");
              try {
                await wallet.provider.request({
                  method: "wallet_revokePermissions",
                  params: [{ eth_accounts: {} }],
                });
                collectLog(logsAndVars, "Revoke permissions is done");
              } catch (error) {
                dwReportError(null, undefined, error, { logsAndVars });
                console.error("Failed to revoke permissions:", error);
              }
            }
      
            */
        }
        else {
            collectLog(logsAndVars, "We didn't have a wallet");
            context.onLogout();
        }
    }, [selectedWallet, wallets, context, invalidateBackendAuth]);
    const loadPrevWalletRdns = () => {
        const savedPrevWalletRdns = localStorage.getItem("prevWalletRdns");
        setPrevWalletRdns(savedPrevWalletRdns);
        return savedPrevWalletRdns;
    };
    const updatePrevWalletRdns = (prevWalletRdns) => {
        setPrevWalletRdns(prevWalletRdns);
        if (prevWalletRdns !== null) {
            localStorage.setItem("prevWalletRdns", prevWalletRdns);
        }
        else {
            localStorage.removeItem("prevWalletRdns");
        }
    };
    const loadSelectedWalletRdns = () => {
        const savedSelectedWalletRdns = localStorage.getItem("selectedWalletRdns");
        return savedSelectedWalletRdns;
    };
    const updateSelectedWallet = (selectedWallet) => {
        setSelectedWallet(selectedWallet);
        if (selectedWallet !== null) {
            localStorage.setItem("selectedWalletRdns", selectedWallet.info.rdns);
        }
        else {
            localStorage.removeItem("selectedWalletRdns");
        }
    };
    /*const getSelectedWallet = () => {
      return selectedWalletRdns === null ? null : wallets[selectedWalletRdns];
    };*/
    const isConnected = () => {
        return selectedWallet !== null;
    };
    const isLoggedIn = () => {
        return (selectedWallet !== null
            && selectedAccount !== null
            && hasProtectedWalletAccess(backendAuthStatusForWallet, whitelistStatus));
    };
    const isWalletChanged = () => {
        return loadPrevWalletRdns() !== loadSelectedWalletRdns();
    };
    const getSelectedWalletRdns = () => {
        if (selectedWallet === null) {
            return null;
        }
        else {
            return selectedWallet.info.rdns;
        }
    };
    const contextValue = {
        wallets,
        selectedWallet: selectedWallet,
        selectedAccount,
        errorMessage,
        connectWallet,
        disconnectWallet,
        clearError,
        isLoggedIn, // user logged in in the past, and has not logged out
        isConnected, // isLoggedIn AND user has an active connection with the system
        isWalletChanged,
        walletSessionVersion,
        backendAuthStatus: backendAuthStatusForWallet,
        isRestoringSession,
        allowInteractiveAuth,
        authoritativeBackendDenial: backendAuthStatus === "denied" &&
            authoritativeDenial?.walletAddress === selectedAccount?.toLowerCase() &&
            authoritativeDenial?.sessionVersion === walletSessionVersion,
        whitelistStatus,
        whitelistUnavailableReason,
        retryWhitelistCheck,
        setBackendAuthStatus,
    };
    return _jsx(WalletProviderContext.Provider, { value: contextValue, children: children });
};
